/**
 * Propriétés d'un bloc déduites du JSON Schema de son contrat (ADR-141 §2.3 : « panneau de propriétés
 * généré du JSON Schema, aucun panneau écrit à la main par type »). Générique : le métier (formulaire,
 * page web, page GS1) fournit le schéma, l'ordre, les libellés et quelques éditeurs dédiés ; le socle
 * lit types, énumérations, bornes, obligations et références.
 */

export type SchemaNode = Record<string, unknown>;
/** Schémas par `$id` (`inspection-template-body.schema.json`), comme le paquet des contrats les sert. */
export type SchemaRegistry = Readonly<Record<string, SchemaNode>>;

export type PropertyKind = 'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'localized' | 'expression' | 'object' | 'string_list' | 'list' | 'union' | 'map';

export interface PropertyDescriptor {
  key: string;
  kind: PropertyKind;
  description?: string;
  required: boolean;
  enumValues?: string[];
  minimum?: number;
  maximum?: number;
  pattern?: string;
  defaultValue?: unknown;
  /** Propriétés d'un objet imbriqué (`kind` = `object`). */
  children?: PropertyDescriptor[];
  /** Schéma résolu, pour un éditeur dédié. */
  schema: SchemaNode;
  /** `$ref` d'origine, s'il y en avait une (`expression.schema.json`). */
  ref?: string;
}

const isRecord = (value: unknown): value is SchemaNode => value !== null && typeof value === 'object' && !Array.isArray(value);

/** Résout une `$ref` relative au document `base` (`#/$defs/x`) ou à un autre contrat (`enums.schema.json#/$defs/x`). */
export function resolveRef(ref: string, base: SchemaNode, registry: SchemaRegistry): { node: SchemaNode; document: SchemaNode } | null {
  const [file, fragment = ''] = ref.split('#');
  const document = file === undefined || file === '' ? base : registry[file];
  if (document === undefined) return null;
  let node: unknown = document;
  for (const segment of fragment.split('/').filter((part) => part !== '')) node = isRecord(node) ? node[segment.replace(/~1/g, '/').replace(/~0/g, '~')] : undefined;
  return isRecord(node) ? { node, document } : null;
}

const LOCALIZED = /#\/\$defs\/localized_text$/;
const EXPRESSION = /^expression\.schema\.json(#.*)?$/;

/** Suit les `$ref` (et un `allOf` d'une seule référence) jusqu'au schéma concret. */
export function concrete(node: SchemaNode, document: SchemaNode, registry: SchemaRegistry, depth = 0): { node: SchemaNode; document: SchemaNode; ref?: string } {
  const ref = typeof node['$ref'] === 'string' ? node['$ref'] : undefined;
  if (ref === undefined || depth > 8 || LOCALIZED.test(ref) || EXPRESSION.test(ref)) return ref === undefined ? { node, document } : { node, document, ref };
  const resolved = resolveRef(ref, document, registry);
  if (resolved === null) return { node, document, ref };
  // Les mots-clés posés à côté de la `$ref` (description) l'emportent sur la cible.
  const merged = { ...resolved.node, ...Object.fromEntries(Object.entries(node).filter(([key]) => key !== '$ref')) };
  const next = concrete(merged, resolved.document, registry, depth + 1);
  return next.ref === undefined ? { ...next, ref } : next;
}

function kindOf(node: SchemaNode, ref: string | undefined): PropertyKind {
  if (ref !== undefined && LOCALIZED.test(ref)) return 'localized';
  if (ref !== undefined && EXPRESSION.test(ref)) return 'expression';
  if (Array.isArray(node['enum'])) return 'enum';
  // Un objet à propriétés reste un objet, même s'il porte des alternatives d'obligation (`anyOf` de `required`).
  if (isRecord(node['properties'])) return 'object';
  if (Array.isArray(node['anyOf']) || Array.isArray(node['oneOf'])) return 'union';
  switch (node['type']) {
    case 'boolean':
      return 'boolean';
    case 'integer':
      return 'integer';
    case 'number':
      return 'number';
    case 'string':
      return 'string';
    case 'array': {
      const items = isRecord(node['items']) ? node['items'] : {};
      return items['type'] === 'string' && items['$ref'] === undefined ? 'string_list' : 'list';
    }
    case 'object':
      return isRecord(node['properties']) ? 'object' : 'map';
    default:
      return isRecord(node['properties']) ? 'object' : 'union';
  }
}

export interface DescribeOptions {
  /** Ordre et liste des clés montrées, par chemin d'objet (`''` racine, `presentation`, `target`…). Absent : toutes, dans l'ordre du schéma. */
  include?: Readonly<Record<string, readonly string[]>>;
}

/** Propriétés d'un objet du schéma, récursivement pour les objets imbriqués. */
export function describeProperties(schema: SchemaNode, registry: SchemaRegistry, options: DescribeOptions = {}, path = '', document: SchemaNode = schema): PropertyDescriptor[] {
  const resolved = concrete(schema, document, registry);
  const properties = isRecord(resolved.node['properties']) ? resolved.node['properties'] : {};
  const required = new Set(Array.isArray(resolved.node['required']) ? (resolved.node['required'] as string[]) : []);
  const keys = options.include?.[path] ?? Object.keys(properties);
  const out: PropertyDescriptor[] = [];
  for (const key of keys) {
    const raw = properties[key];
    if (!isRecord(raw)) continue;
    const target = concrete(raw, resolved.document, registry);
    const kind = kindOf(target.node, target.ref);
    const descriptor: PropertyDescriptor = { key, kind, required: required.has(key), schema: target.node };
    if (target.ref !== undefined) descriptor.ref = target.ref;
    if (typeof target.node['description'] === 'string') descriptor.description = target.node['description'];
    if (Array.isArray(target.node['enum'])) descriptor.enumValues = (target.node['enum'] as unknown[]).map(String);
    if (typeof target.node['minimum'] === 'number') descriptor.minimum = target.node['minimum'];
    if (typeof target.node['maximum'] === 'number') descriptor.maximum = target.node['maximum'];
    if (typeof target.node['pattern'] === 'string') descriptor.pattern = target.node['pattern'];
    if ('default' in target.node) descriptor.defaultValue = target.node['default'];
    if (kind === 'object') descriptor.children = describeProperties(target.node, registry, options, path === '' ? key : `${path}.${key}`, target.document);
    out.push(descriptor);
  }
  return out;
}

/** Contrôle local d'une valeur primitive contre son descripteur : aide de saisie, le serveur tranche. */
export function primitiveIssue(descriptor: PropertyDescriptor, value: unknown): string | null {
  if (value === undefined || value === null || value === '') return descriptor.required ? 'Obligatoire.' : null;
  if ((descriptor.kind === 'number' || descriptor.kind === 'integer') && typeof value === 'number') {
    if (descriptor.kind === 'integer' && !Number.isInteger(value)) return 'Un nombre entier.';
    if (descriptor.minimum !== undefined && value < descriptor.minimum) return `Au moins ${descriptor.minimum}.`;
    if (descriptor.maximum !== undefined && value > descriptor.maximum) return `Au plus ${descriptor.maximum}.`;
  }
  if (descriptor.kind === 'string' && typeof value === 'string' && descriptor.pattern !== undefined && !new RegExp(descriptor.pattern).test(value)) return 'Format non admis (minuscules, chiffres et « _ », commençant par une lettre).';
  if (descriptor.kind === 'enum' && descriptor.enumValues !== undefined && !descriptor.enumValues.includes(String(value))) return 'Valeur non admise.';
  return null;
}
